import { FormsHelpers } from '@bimser/common';
import * as LayoutCreatorEntities from '@bimser/components/dist/BCLayoutCreator/common/entities';
import { FormItemTypes, IDictionary } from '@bimser/core';
import { Map } from 'immutable';
import cloneDeep from 'lodash/cloneDeep';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import mergeWith from 'lodash/mergeWith';
import {
	IRelatedDocumentCategory,
	IRelatedDocumentFile
} from '../environments/runtime/components/RelatedDocuments/entities';

export function deepMergeFormItems(
	currentFormData: LayoutCreatorEntities.INormalizedLayoutItems,
	updatedFormData: LayoutCreatorEntities.INormalizedLayoutItems,
	windowId?: string,
	type?: 'Save' | 'Run'
): LayoutCreatorEntities.INormalizedLayoutItems {
	if (updatedFormData) {
		updatedFormData = cloneDeep(
			clearUnnecessaryProperties(updatedFormData, currentFormData, type)
		);
		const { data, deletedItems } = clearEmptyObjects(updatedFormData);
		const formItemId = currentFormData.result[0];
		const items = currentFormData.entities.items;
		const workFlowInfo = items[formItemId].properties?.responseParameters?.workflowInfo;
		const validate =
			workFlowInfo?.events?.find(item => item.id === workFlowInfo?.event?.id)?.validate ??
			true;
		const updated = FormsHelpers.showServerSideMessages(data, windowId, validate);
		const current = clearDeletedItems(currentFormData, deletedItems);

		return mergeWith(
			increaseCommitId(current, updated),
			updated,
			(objValue: any, srcValue: any) => {
				if (isArray(objValue)) {
					return srcValue;
				}
			}
		);
	}

	return currentFormData;
}

function clearUnnecessaryProperties(
	data: LayoutCreatorEntities.INormalizedLayoutItems,
	currentFormData: LayoutCreatorEntities.INormalizedLayoutItems,
	type: 'Save' | 'Run'
) {
	Object.keys(data.entities.items).forEach(id => {
		const item = cloneDeep(data.entities?.items[id]);
		const itemType = currentFormData?.entities?.items[id]?.type;

		if (item?.properties) {
			switch (itemType) {
				case FormItemTypes.EControlTypes.ComboBox:
				case FormItemTypes.EControlTypes.RadioList:
				case FormItemTypes.EControlTypes.ListBox: {
					if (item.properties?.items) {
						item.properties.items = item.properties.items.map(element => {
							delete element.Field;
							if (!element.icon) {
								element.icon = undefined;
							}

							return element;
						});
					}
					break;
				}
				case FormItemTypes.EControlTypes.RelatedDocuments:
					if (type === 'Save') {
						delete item.properties.targetSecretKey;

						item.properties.categories?.forEach(
							(category: IRelatedDocumentCategory) => {
								delete category.targetSecretKey;
							}
						);

						item.properties.files?.forEach((file: IRelatedDocumentFile) => {
							delete file.secretKey;
						});
					}
					break;
				default:
					break;
			}

			data.entities.items[id] = item;
		}
	});

	return data;
}

export function isDMSecretModified(
	data: LayoutCreatorEntities.INormalizedLayoutItems,
	currentFormData: LayoutCreatorEntities.INormalizedLayoutItems,
	controlType: FormItemTypes.EControlTypes
) {
	return Object.keys(data.entities.items).some(id => {
		const itemType = currentFormData?.entities?.items[id]?.type;
		const item = data.entities.items[id];

		if (item &&
			itemType === controlType &&
			((controlType === FormItemTypes.EControlTypes.DocumentViewer &&
				(item.properties?.targetPath || item.properties?.targetSecretKey)) ||
				(controlType === FormItemTypes.EControlTypes.RelatedDocuments &&
					(item.properties?.targetSecretKey ||
						item.properties?.categories ||
						item.properties?.files)))
		) {
			return true;
		}

		return false;
	});
}

export function normalizeItemsForServer(data: LayoutCreatorEntities.INormalizedLayoutItems) {
	const items = data.entities.items;

	for (const i in items) {
		delete items[i].properties.commitId;

		const columnOrRow =
			items[i].type === FormItemTypes.EContainerTypes.Column ||
			items[i].type === FormItemTypes.EContainerTypes.Section;
		if (columnOrRow) {
			items[i].properties = null;
		}

		items[i] = filterItemPropertiesByItemType(items[i]);
	}

	data.entities.items = items;

	return data;
}

export function filterItemPropertiesByItemType(item: LayoutCreatorEntities.ILayoutItem) {
	switch (item.type) {
		case FormItemTypes.EControlTypes.DocumentViewer: {
			delete item.properties.isDocumentLoading;
			break;
		}
	}

	return item;
}

export function mergeDataAndView(
	data: Map<string, LayoutCreatorEntities.LayoutDataItem>,
	view: LayoutCreatorEntities.NormalizedLayoutStructureItems
) {
	let newView = LayoutCreatorEntities.NormalizedLayoutItemsFactoryFromJS(view.toJS());
	let newItems = newView.entities.items;
	let newGhostItems = newView.entities.ghostItems;

	data.forEach(dataItem => {
		if (newItems.has(dataItem.id)) {
			newItems = newItems.setIn([dataItem.id, 'properties'], dataItem.properties);
		} else if (newGhostItems.has(dataItem.id)) {
			newGhostItems = newGhostItems.setIn([dataItem.id, 'properties'], dataItem.properties);
		}
	});

	newView = newView
		.setIn(['entities', 'items'], newItems)
		.setIn(['entities', 'ghostItems'], newGhostItems);
	return newView;
}

function clearEmptyObjects(data: LayoutCreatorEntities.INormalizedLayoutItems) {
	const currentItems = data.entities.items;
	const currentGhostItems = data.entities.ghostItems;

	const newItems: IDictionary<LayoutCreatorEntities.ILayoutItem> = {};
	const newGhostItems: IDictionary<LayoutCreatorEntities.ILayoutItem> = {};

	const deletedItems: string[] = [];

	for (const key in currentItems) {
		if (Object.prototype.hasOwnProperty.call(currentItems, key)) {
			const element = currentItems[key];

			if (element) {
				newItems[key] = element;
			} else {
				deletedItems.push(key);
			}
		}
	}

	for (const key in currentGhostItems) {
		if (Object.prototype.hasOwnProperty.call(currentGhostItems, key)) {
			const element = currentGhostItems[key];

			if (element) {
				newGhostItems[key] = element;
			} else {
				deletedItems.push(key);
			}
		}
	}

	data.entities.items = newItems;
	data.entities.ghostItems = newGhostItems;

	return {
		data,
		deletedItems
	};
}

function clearDeletedItems(
	data: LayoutCreatorEntities.INormalizedLayoutItems,
	deletedItems: string[]
) {
	const currentItems = data.entities.items;
	const currentGhostItems = data.entities.ghostItems;

	deletedItems.forEach(i => {
		delete currentItems[i];
		delete currentGhostItems[i];
	});

	data.entities.items = currentItems;
	data.entities.ghostItems = currentGhostItems;

	return data;
}

function increaseCommitId(
	current: LayoutCreatorEntities.INormalizedLayoutItems,
	updated: LayoutCreatorEntities.INormalizedLayoutItems
) {
	Object.keys(updated.entities.items).forEach(controlName => {
		const currentControl =
			current.entities.items[controlName] || current.entities.ghostItems[controlName];
		const updatedControl =
			updated.entities.items[controlName] || updated.entities.ghostItems[controlName];

		if (
			currentControl &&
			!isEmpty(currentControl.properties) &&
			!isEmpty(updatedControl.properties)
		) {
			const isForm = currentControl.type === FormItemTypes.EContainerTypes.Form;
			const isPanelBase = Object.values(FormItemTypes.EPanelBaseTypes).includes(
				currentControl.type as FormItemTypes.EPanelBaseTypes
			);

			const [anyChange, effectiveKey] = isThereAnyChange(
				currentControl.properties,
				updatedControl.properties,
				isPanelBase || isForm
			);

			if (anyChange) {
				if (Number.isInteger(currentControl.properties.commitId)) {
					currentControl.properties.commitId++;
				} else {
					currentControl.properties.commitId = 1;
				}

				if (effectiveKey) {
					const shouldIncreaseKeys: string[] = [];

					if (isForm) {
						shouldIncreaseKeys.push(
							...Object.keys({
								...current.entities.items,
								...current.entities.ghostItems
							})
						);
					} else if (isPanelBase) {
						const getPanelChilds = (controlName: string) => {
							shouldIncreaseKeys.push(controlName);

							(current.entities.items[controlName].items as string[]).forEach(
								getPanelChilds
							);
						};

						(current.entities.items[currentControl.id].items as string[]).forEach(
							getPanelChilds
						);
					}

					shouldIncreaseKeys.forEach(_controlName => {
						const control =
							current.entities.items[_controlName] ||
							current.entities.ghostItems[_controlName];

						const mustIncreaseList: string[] = Object.values({
							...FormItemTypes.EControlTypes,
							...FormItemTypes.EPanelBaseTypes
						});

						if (mustIncreaseList.includes(control.type)) {
							if (Number.isInteger(control.properties.commitId)) {
								control.properties.commitId++;
							} else {
								control.properties.commitId = 0;
							}
						}
					});
				}
			}
		}
	});

	return current;
}

function isThereAnyChange(
	current: IDictionary<any>,
	updated: IDictionary<any>,
	isFormOrPanelPase: boolean
) {
	let effectiveKey: string;
	let anyChange: boolean = false;

	Object.keys(updated).forEach(key => {
		if (current.hasOwnProperty(key)) {
			if (!isEqual(current[key], updated[key])) {
				if (
					isFormOrPanelPase &&
					!effectiveKey &&
					['readOnly', 'clientEnabled', 'enabled'].includes(key)
				) {
					effectiveKey = key;
				}

				anyChange = true;
			}
		}
	});

	return [anyChange, effectiveKey];
}
